import { Fragment, h } from "preact"
import { useEffect, useMemo, useRef, useState } from "preact/hooks"
import { createPortal } from "preact/compat"
import axios from "axios"
import Badge from "./Badge"
import { PingResultType } from "../api/types"
import {
   autoUpdate,
   computePosition,
   offset,
   flip,
   shift,
   arrow as arrowMiddleware,
} from "@floating-ui/dom"
import List from "../assets/list.svg"
import Chart from "../assets/chart.svg"
import EyeIcon from "../assets/eye-icon.svg"
import { DISPLAY_TIME_ZONE } from "../utils/time"
// Charting
import uPlot from "uplot"
import "uplot/dist/uPlot.min.css"

type Sample = { time: number; value: number | null }

type Variant = "value" | "actions" | "both"

export default function LatencyPopoverCell({
   row,
   variant = "both",
}: {
   row: PingResultType
   variant?: Variant
}) {
   const btnRef = useRef<HTMLSpanElement>(null)
   const floatingRef = useRef<HTMLDivElement>(null)
   const arrowRef = useRef<HTMLDivElement>(null)

   // Chart popover refs
   const chartBtnRef = useRef<HTMLImageElement>(null)
   const chartFloatingRef = useRef<HTMLDivElement>(null)
   const chartArrowRef = useRef<HTMLDivElement>(null)
   const chartContainerRef = useRef<HTMLDivElement>(null)
   const chartInstanceRef = useRef<uPlot | null>(null)

   const [open, setOpen] = useState(false)
   const [chartOpen, setChartOpen] = useState(false)
   const [loading, setLoading] = useState(false)
   const [error, setError] = useState<string | null>(null)
   const [samples, setSamples] = useState<Sample[]>([])
   const [infoOpen, setInfoOpen] = useState(false)
   const [chartHoverTimeMs, setChartHoverTimeMs] = useState<number | null>(null)
   const [chartHoverValue, setChartHoverValue] = useState<number | null>(null)

   // prevent race conditions (use a "token" for the latest request)
   const lastRequestKeyRef = useRef<string | null>(null)
   const abortRef = useRef<AbortController | null>(null)

   const apiBase = (process.env.PREACT_APP_API_URL as string) || "http://localhost:3005/"

   const requestKey = useMemo(() => {
      const payload = (row as any).payloadSize ?? "-"
      return `${row.ip}|${payload}`
   }, [row.ip, (row as any).payloadSize])

   // Close on outside click and Escape
   useEffect(() => {
      if (!open) return
      const onDocClick = (e: MouseEvent) => {
         const t = e.target as Node
         if (btnRef.current?.contains(t)) return
         const pop = document.getElementById(getWrapId())
         if (pop?.contains(t)) return
         setOpen(false)
      }
      const onKey = (e: KeyboardEvent) => {
         if (e.key === "Escape") setOpen(false)
      }
      document.addEventListener("mousedown", onDocClick)
      document.addEventListener("keydown", onKey)
      return () => {
         document.removeEventListener("mousedown", onDocClick)
         document.removeEventListener("keydown", onKey)
      }
   }, [open])

   // Close chart popover on outside click and Escape
   useEffect(() => {
      if (!chartOpen) return
      const onDocClick = (e: MouseEvent) => {
         const t = e.target as Node
         if (chartBtnRef.current?.contains(t)) return
         const pop = document.getElementById(getChartWrapId())
         if (pop?.contains(t)) return
         setChartOpen(false)
      }
      const onKey = (e: KeyboardEvent) => {
         if (e.key === "Escape") setChartOpen(false)
      }
      document.addEventListener("mousedown", onDocClick)
      document.addEventListener("keydown", onKey)
      return () => {
         document.removeEventListener("mousedown", onDocClick)
         document.removeEventListener("keydown", onKey)
      }
   }, [chartOpen])

   // Close info popover on outside click and Escape
   useEffect(() => {
      if (!infoOpen) return
      const onDocClick = (e: MouseEvent) => {
         const t = e.target as Node
         if (infoBtnRef.current?.contains(t)) return
         const pop = document.getElementById(getInfoWrapId())
         if (pop?.contains(t)) return
         setInfoOpen(false)
      }
      const onKey = (e: KeyboardEvent) => {
         if (e.key === "Escape") setInfoOpen(false)
      }
      document.addEventListener("mousedown", onDocClick)
      document.addEventListener("keydown", onKey)
      return () => {
         document.removeEventListener("mousedown", onDocClick)
         document.removeEventListener("keydown", onKey)
      }
   }, [infoOpen])

   useEffect(() => {
      return () => {
         // cleanup of any in-progress request
         abortRef.current?.abort()
         chartInstanceRef.current?.destroy()
         chartInstanceRef.current = null
      }
   }, [])

   // Floating UI: update position on open and on layout/scroll changes
   useEffect(() => {
      if (!open) return
      const referenceEl = btnRef.current
      const floatingEl = floatingRef.current
      if (!referenceEl || !floatingEl) return

      const update = () => {
         const arrowEl = arrowRef.current
         computePosition(referenceEl, floatingEl, {
            strategy: "fixed",
            placement: "bottom",
            middleware: [
               offset(6),
               flip({ fallbackPlacements: ["top", "right", "left"] }),
               // Allow horizontal shift to keep within viewport while trying to stay centered
               shift({ padding: 8, crossAxis: true }),
               ...(arrowEl ? [arrowMiddleware({ element: arrowEl })] : []),
            ],
         }).then(({ x, y, placement, middlewareData }) => {
            Object.assign(floatingEl.style, { left: `${x}px`, top: `${y}px` })
            floatingEl.setAttribute("data-popper-placement", placement)

            // arrow placement
            const ad = middlewareData.arrow as { x?: number; y?: number } | undefined
            if (arrowEl && ad) {
               const side = placement.split("-")[0] as "top" | "bottom" | "left" | "right"
               const staticSide = { top: "bottom", bottom: "top", left: "right", right: "left" }[
                  side
               ]
               arrowEl.style.left = ad.x != null ? `${ad.x}px` : ""
               arrowEl.style.top = ad.y != null ? `${ad.y}px` : ""
               arrowEl.style.right = ""
               arrowEl.style.bottom = ""
               ;(arrowEl.style as any)[staticSide] = "-4px"
            }
         })
      }

      const cleanup = autoUpdate(referenceEl, floatingEl, update, { animationFrame: true })
      update()
      return () => cleanup()
   }, [open])

   // Floating UI for chart popover
   useEffect(() => {
      if (!chartOpen) return
      const referenceEl = chartBtnRef.current
      const floatingEl = chartFloatingRef.current
      if (!referenceEl || !floatingEl) return

      const update = () => {
         const arrowEl = chartArrowRef.current
         computePosition(referenceEl, floatingEl, {
            strategy: "fixed",
            placement: "bottom",
            middleware: [
               offset(6),
               flip({ fallbackPlacements: ["top", "right", "left"] }),
               shift({ padding: 8, crossAxis: true }),
               ...(arrowEl ? [arrowMiddleware({ element: arrowEl })] : []),
            ],
         }).then(({ x, y, placement, middlewareData }) => {
            // Center horizontally in the viewport
            const width = floatingEl.offsetWidth || 0
            const viewportW = window.innerWidth || document.documentElement.clientWidth || 0
            const centeredLeft = Math.max(0, Math.round((viewportW - width) / 2))
            Object.assign(floatingEl.style, { left: `${centeredLeft}px`, top: `${y}px` })
            floatingEl.setAttribute("data-popper-placement", placement)

            // arrow placement
            if (arrowEl) {
               const side = placement.split("-")[0] as "top" | "bottom" | "left" | "right"
               const staticSide = { top: "bottom", bottom: "top", left: "right", right: "left" }[
                  side
               ]
               // Position arrow to point to the trigger horizontally
               const btnRect = referenceEl.getBoundingClientRect()
               const btnCenter = btnRect.left + btnRect.width / 2
               const arrowLeft = Math.round(btnCenter - centeredLeft)
               arrowEl.style.left = `${arrowLeft}px`
               arrowEl.style.top = ""
               arrowEl.style.right = ""
               arrowEl.style.bottom = ""
               ;(arrowEl.style as any)[staticSide] = "-4px"
            }
         })
      }

      const cleanup = autoUpdate(referenceEl, floatingEl, update, { animationFrame: true })
      update()
      return () => cleanup()
   }, [chartOpen])

   // Floating UI for info popover
   useEffect(() => {
      if (!infoOpen) return
      const referenceEl = infoBtnRef.current
      const floatingEl = infoFloatingRef.current
      if (!referenceEl || !floatingEl) return

      const update = () => {
         const arrowEl = infoArrowRef.current
         computePosition(referenceEl, floatingEl, {
            strategy: "fixed",
            placement: "bottom",
            middleware: [
               offset(6),
               flip({ fallbackPlacements: ["top", "right", "left"] }),
               shift({ padding: 8, crossAxis: true }),
               ...(arrowEl ? [arrowMiddleware({ element: arrowEl })] : []),
            ],
         }).then(({ x, y, placement, middlewareData }) => {
            Object.assign(floatingEl.style, { left: `${x}px`, top: `${y}px` })
            floatingEl.setAttribute("data-popper-placement", placement)

            const ad = middlewareData.arrow as { x?: number; y?: number } | undefined
            if (arrowEl && ad) {
               const side = placement.split("-")[0] as "top" | "bottom" | "left" | "right"
               const staticSide = { top: "bottom", bottom: "top", left: "right", right: "left" }[
                  side
               ]
               arrowEl.style.left = ad.x != null ? `${ad.x}px` : ""
               arrowEl.style.top = ad.y != null ? `${ad.y}px` : ""
               arrowEl.style.right = ""
               arrowEl.style.bottom = ""
               ;(arrowEl.style as any)[staticSide] = "-4px"
            }
         })
      }

      const cleanup = autoUpdate(referenceEl, floatingEl, update, { animationFrame: true })
      update()
      return () => cleanup()
   }, [infoOpen])

   const getWrapId = () =>
      `lp-${row.ip.replace(/[^a-zA-Z0-9_-]/g, "_")}-${(row as any).payloadSize ?? "n"}`
   const getChartWrapId = () =>
      `lp-chart-${row.ip.replace(/[^a-zA-Z0-9_-]/g, "_")}-${(row as any).payloadSize ?? "n"}`
   const getInfoWrapId = () =>
      `lp-info-${row.ip.replace(/[^a-zA-Z0-9_-]/g, "_")}-${(row as any).payloadSize ?? "n"}`

   const fetchData = async () => {
      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller

      setLoading(true)
      setError(null)

      // token to protect against stale responses
      lastRequestKeyRef.current = requestKey
      try {
         const params = new URLSearchParams({ ip: row.ip })
         const payload = (row as any).payloadSize
         // Backend expects payload (null/undefined treated as 56 on server; we pass explicit 56 when unset)
         params.append("payload", String(payload ?? 56))
         const url = apiBase.replace(/\/$/, "") + "/pings?" + params.toString()
         const res = await axios.get(url, { signal: controller.signal })
         const list: Sample[] = Array.isArray(res.data?.samples) ? res.data.samples : []
         list.sort((a, b) => b.time - a.time)

         // apply data only if this is still the latest request
         if (lastRequestKeyRef.current === requestKey) {
            setSamples(list)
         }
      } catch (e: any) {
         if (e?.name === "CanceledError" || e?.message === "canceled") return
         setError("Nepodařilo se načíst data")
      } finally {
         if (lastRequestKeyRef.current === requestKey) setLoading(false)
      }
   }

   const openPopover = async () => {
      setOpen(true)
      // don't clear samples immediately — show the last known state,
      // and indicate loading separately
      await Promise.resolve()
      fetchData()
   }

   const openChart = async () => {
      setChartOpen(true)
      await Promise.resolve()
      fetchData()
   }

   const onHistoryClick = (e: MouseEvent) => {
      // Stop bubbling to surrounding handlers (e.g., table row selection)
      e.preventDefault()
      e.stopPropagation()
      if (!open) openPopover()
      else setOpen(false)
   }

   const onChartClick = (e: MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      if (!chartOpen) openChart()
      else setChartOpen(false)
   }

   const infoBtnRef = useRef<HTMLImageElement>(null)
   const infoFloatingRef = useRef<HTMLDivElement>(null)
   const infoArrowRef = useRef<HTMLDivElement>(null)

   const onInfoClick = (e: MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setInfoOpen((v) => !v)
   }

   const isOffline = (row as any).ping === undefined

   // value displayed in the cell
   const pingValue = useMemo(
      () => String(Number.parseInt((row as any).ping as unknown as any)),
      [row],
   )

   // Setup or update the chart when open and samples change
   useEffect(() => {
      if (!chartOpen) return
      const container = chartContainerRef.current
      if (!container) return

      const asc = [...samples].sort((a, b) => a.time - b.time)
      const x = asc.map((s) => Math.floor(s.time / 1000))
      // Show undefined/null samples as -1 in the chart (instead of gaps)
      const y = asc.map((s) => (s.value == null ? -1 : Number(s.value)))

      const data: (number | null)[][] = [x, y]

      if (!chartInstanceRef.current) {
         const width = Math.max(360, container.clientWidth || 420)
         const u = new uPlot(
            {
               width,
               height: 220,
               scales: { x: { time: true } },
               series: [
                  {},
                  {
                     label: "ms",
                     stroke: "#0d6efd",
                     width: 1.5,
                     spanGaps: true,
                  },
               ],
               axes: [
                  {
                     stroke: "#6c757d",
                     values: (_u: uPlot, vals: number[]) =>
                        vals.map((v) =>
                           new Date(v * 1000).toLocaleTimeString("cs-CZ", {
                              timeZone: DISPLAY_TIME_ZONE,
                              hour12: false,
                              hour: "2-digit",
                              minute: "2-digit",
                              second: "2-digit",
                           }),
                        ),
                  },
                  {
                     stroke: "#6c757d",
                     grid: { show: true },
                     label: "[ms]",
                     labelSize: 24,
                     values: (_u: uPlot, vals: number[]) =>
                        vals.map((v: number) => String(Math.round(v))),
                  },
               ],
               legend: { show: false },
               hooks: {
                  setCursor: [
                     (u: uPlot) => {
                        const idx = (u as any).cursor.idx as number | null | undefined
                        if (idx != null && idx >= 0) {
                           const tsSec = (u.data[0] as number[])[idx]
                           setChartHoverTimeMs(tsSec * 1000)
                           const val = (u.data[1] as (number | null)[])[idx]
                           setChartHoverValue(val == null ? null : Number(val))
                        } else {
                           setChartHoverTimeMs(null)
                           setChartHoverValue(null)
                        }
                     },
                  ],
               },
            },
            data as any,
            container,
         )
         chartInstanceRef.current = u

         // Simple resize handler
         const ro = new ResizeObserver(() => {
            try {
               const w = Math.max(360, container.clientWidth || 420)
               u.setSize({ width: w, height: 220 })
            } catch {}
         })
         ro.observe(container)
         ;(u as any)._ro = ro
      } else {
         chartInstanceRef.current.setData(data as any)
      }
   }, [chartOpen, samples])

   // Cleanup chart instance when the chart popover closes
   useEffect(() => {
      if (!chartOpen && chartInstanceRef.current) {
         try {
            const u = chartInstanceRef.current as any
            const ro: ResizeObserver | undefined = u._ro
            if (ro && chartContainerRef.current) ro.unobserve(chartContainerRef.current)
         } catch {}
         chartInstanceRef.current.destroy()
         chartInstanceRef.current = null
      }
   }, [chartOpen])

   return (
      <Fragment>
         {/* Value-only view */}
         {variant !== "actions" && (
            <div className="d-flex align-items-center gap-2">
               {isOffline ? (
                  <Badge label="Offline" color="danger" />
               ) : (
                  <span style={{ color: "#212529" }}>{pingValue}</span>
               )}
            </div>
         )}

         {/* Actions-only view */}
         {variant !== "value" && (
            <div className="d-flex align-items-center" style={{ gap: 8 }}>
               <img
                  ref={infoBtnRef}
                  src={EyeIcon as any}
                  width={14}
                  height={14}
                  alt="detaily paketu"
                  title="Detaily"
                  onClick={onInfoClick as any}
                  style={{ opacity: 0.6, cursor: "pointer" }}
               />
               <span
                  ref={btnRef}
                  role="button"
                  tabIndex={0}
                  onClick={onHistoryClick as any}
                  style={{ display: "inline-flex", alignItems: "center", cursor: "pointer" }}
                  title="Historie"
               >
                  <img
                     src={List as any}
                     width={14}
                     height={14}
                     alt="historie"
                     style={{ opacity: 0.6 }}
                  />
               </span>
               <img
                  ref={chartBtnRef}
                  src={Chart as any}
                  width={14}
                  height={14}
                  alt="graf"
                  title="Graf"
                  onClick={onChartClick as any}
                  style={{ opacity: 0.6, cursor: "pointer" }}
               />
            </div>
         )}

         {open &&
            createPortal(
               <div
                  id={getWrapId()}
                  ref={floatingRef}
                  className="popover bs-popover-auto show"
                  style={{
                     position: "fixed",
                     display: "block",
                     width: "fit-content",
                     zIndex: 1060,
                     maxWidth: 420,
                     top: 0,
                     left: 0,
                  }}
                  onMouseDown={(e) => {
                     e.stopPropagation()
                  }}
                  onClick={(e) => {
                     e.stopPropagation()
                  }}
               >
                  <div className="popover-arrow" ref={arrowRef} />
                  <h3 className="popover-header">
                     <div>Historie odezvy</div>
                     <div>
                        {row.ip}{" "}
                        {(row as any).payloadSize != null && ` (${(row as any).payloadSize}B)`}
                     </div>
                  </h3>
                  <div
                     className="popover-body"
                     style={{
                        padding: 8,
                        maxHeight: 500,
                        overflowY: "auto",
                     }}
                  >
                     <table className="table table-sm mb-0">
                        <thead>
                           <tr>
                              <th>Čas</th>
                              <th className="text-end">Latence [ms]</th>
                           </tr>
                        </thead>
                        <tbody>
                           {loading && (
                              <tr>
                                 <td colSpan={2}>Načítám…</td>
                              </tr>
                           )}
                           {!loading && error && (
                              <tr>
                                 <td colSpan={2}>{error}</td>
                              </tr>
                           )}
                           {!loading && !error && samples.length === 0 && (
                              <tr>
                                 <td colSpan={2}>Žádná data</td>
                              </tr>
                           )}
                           {!loading &&
                              !error &&
                              samples.map((s) => (
                                 <tr key={s.time}>
                                    <td>
                                       {new Date(s.time).toLocaleTimeString("cs-CZ", {
                                          timeZone: DISPLAY_TIME_ZONE,
                                          hour12: false,
                                          hour: "2-digit",
                                          minute: "2-digit",
                                          second: "2-digit",
                                       })}
                                    </td>
                                    <td className="text-end">
                                       {s.value == null
                                          ? "-"
                                          : new Intl.NumberFormat("cs-CZ", {
                                               minimumFractionDigits: 2,
                                               maximumFractionDigits: 2,
                                            }).format(s.value)}
                                    </td>
                                 </tr>
                              ))}
                        </tbody>
                     </table>
                  </div>
               </div>,
               document.body,
            )}

         {chartOpen &&
            createPortal(
               <div
                  id={getChartWrapId()}
                  ref={chartFloatingRef}
                  className="popover bs-popover-auto show"
                  style={{
                     position: "fixed",
                     display: "block",
                     width: "fit-content",
                     zIndex: 1060,
                     maxWidth: "90vw",
                     top: 0,
                     left: 0,
                  }}
                  onMouseDown={(e) => {
                     e.stopPropagation()
                  }}
                  onClick={(e) => {
                     e.stopPropagation()
                  }}
               >
                  <div className="popover-arrow" ref={chartArrowRef} />
                  <h3 className="popover-header">
                     <div className="d-flex justify-content-between align-items-start gap-2 flex-wrap">
                        <div>Graf odezvy</div>
                        <div className="d-flex gap-2" style={{ wordBreak: "break-word" }}>
                           <span>{row.ip}</span>
                           {(row as any).payloadSize != null && (
                              <span>({(row as any).payloadSize}B)</span>
                           )}
                        </div>
                     </div>
                  </h3>
                  <div className="popover-body" style={{ padding: 8 }}>
                     {loading && <div>Načítám…</div>}
                     {!loading && error && <div>{error}</div>}
                     {!loading && !error && samples.length === 0 && <div>Žádná data</div>}
                     <div
                        className="position-relative"
                        style={{
                           position: "relative",
                           width: "90vw",
                           maxWidth: "90vw",
                           minWidth: 360,
                        }}
                     >
                        <div ref={chartContainerRef} style={{ width: "100%" }} />
                        {(chartHoverTimeMs != null || chartHoverValue != null) && (
                           <div
                              style={{
                                 position: "absolute",
                                 left: "50%",
                                 bottom: -2,
                                 transform: "translateX(-50%)",
                                 color: "#212529",
                                 fontSize: 14,
                                 fontWeight: 500,
                                 whiteSpace: "nowrap",
                                 pointerEvents: "none",
                              }}
                           >
                              {chartHoverTimeMs != null
                                 ? new Date(chartHoverTimeMs).toLocaleTimeString("cs-CZ", {
                                      timeZone: DISPLAY_TIME_ZONE,
                                      hour12: false,
                                      hour: "2-digit",
                                      minute: "2-digit",
                                      second: "2-digit",
                                   })
                                 : "—"}
                              <span style={{ opacity: 0.65 }}> · </span>
                              {chartHoverValue != null
                                 ? new Intl.NumberFormat("cs-CZ", {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                   }).format(chartHoverValue) + " ms"
                                 : "-"}
                           </div>
                        )}
                     </div>
                  </div>
               </div>,
               document.body,
            )}

         {infoOpen &&
            createPortal(
               <div
                  id={getInfoWrapId()}
                  ref={infoFloatingRef}
                  className="popover bs-popover-auto show"
                  style={{
                     position: "fixed",
                     display: "block",
                     width: "fit-content",
                     zIndex: 1060,
                     maxWidth: 420,
                     top: 0,
                     left: 0,
                  }}
                  onMouseDown={(e) => {
                     e.stopPropagation()
                  }}
                  onClick={(e) => {
                     e.stopPropagation()
                  }}
               >
                  <div className="popover-arrow" ref={infoArrowRef} />
                  <h3 className="popover-header">
                     <div>Packet size</div>
                     <div>
                        {row.ip}{" "}
                        {(row as any).payloadSize != null && ` (${(row as any).payloadSize}B)`}
                     </div>
                  </h3>
                  <div className="popover-body" style={{ padding: 8 }}>
                     {(() => {
                        const payload = (row as any).payloadSize
                        const hasPayload = payload !== undefined && payload !== null
                        const payloadNum = hasPayload ? Number(payload) : null
                        const sumWithIp = payloadNum != null ? payloadNum + 8 + 20 : null
                        return (
                           <div style={{ whiteSpace: "pre-wrap", fontFamily: "monospace" }}>
                              <div>
                                 <b>packet size:</b>
                              </div>
                              <div class="mt-2">
                                 icmp payload: <b>{hasPayload ? `${payload}B` : "—"}</b>
                              </div>
                              <div>
                                 icmp header: <b>8B</b>
                              </div>
                              <div>
                                 ip header: <b>20B</b> (per packet)
                              </div>
                              <div style={{ marginTop: 8 }}>
                                 nefragmentovaný packet:
                                 <br />
                                 <b>
                                    {hasPayload ? `${payload}B + 8B + 20B = ${sumWithIp}B` : "—"}
                                 </b>
                              </div>
                           </div>
                        )
                     })()}
                  </div>
               </div>,
               document.body,
            )}
      </Fragment>
   )
}
